import { ImportedModelRenderableResourceCollector } from '../../resources/ImportedModelRenderableResourceCollector'
import { ImportedModelResourceOwnershipRegistry } from '../../resources/ImportedModelResourceOwnershipRegistry'
import { ImportedModelResourceScope } from '../../resources/ImportedModelResourceScope'

/**
 * 将 complete 场景分析转换为归档/node 两级资源作用域。
 *
 * `ownedMeshesByNodeId` 是 mesh 所有权的唯一来源；构建过程只登记已有对象引用，不修改
 * GLTFLoader 创建的 Object3D、Geometry、Material 或 Texture。
 */
export class CompleteGlbResourceScopeBuilder {
  /** 统一读取 Mesh 渲染资源的收集器。 */
  private readonly collector = new ImportedModelRenderableResourceCollector()

  /**
   * 创建资源作用域构建器。
   *
   * @param registry 当前归档 scope 使用的资源引用注册表。
   */
  public constructor(
    private readonly registry = new ImportedModelResourceOwnershipRegistry(),
  ) {}

  /**
   * 为 complete GLB 的每个业务 node 建立独立 lease。
   *
   * @param input 完整场景资源根与权威 occurrence 分析。
   * @returns 可整体释放或按 node retain 的归档资源作用域。
   */
  public build(input: PuppyCAD.CompleteGlbResourceScopeBuildInput): ImportedModelResourceScope {
    const scopedNodes = Object.values(input.analysis.manifest.nodesById)
      .filter((node) => node.semanticKind === 'assembly' || node.semanticKind === 'part')
      .map((node) => {
        const root = input.analysis.sourceObjectByNodeId.get(node.nodeId)
        if (!root) {
          throw new Error(`complete GLB 资源作用域缺少 node 根：nodeId=${node.nodeId}`)
        }
        return { node, root }
      })
    const entries: PuppyCAD.ImportedModelResourceScopeEntry[] = []
    try {
      scopedNodes.forEach(({ node, root }) => {
        const objectRoots = node.nodeId === input.analysis.manifest.rootNodeId
          ? [...new Set([input.archiveRoot, root])]
          : [root]
        const resources = this.collector.collect(
          input.analysis.ownedMeshesByNodeId.get(node.nodeId) ?? [],
          objectRoots,
        )
        entries.push(Object.freeze({
          nodeId: node.nodeId,
          root,
          lease: this.registry.acquire(resources),
        }))
      })
      return new ImportedModelResourceScope(entries)
    } catch (error) {
      const rollbackErrors: unknown[] = []
      for (let index = entries.length - 1; index >= 0; index -= 1) {
        try {
          entries[index]?.lease.release()
        } catch (releaseError) {
          // 单个 lease 的底层 dispose 异常不能阻断其它已取得所有权的资源归还。
          rollbackErrors.push(releaseError)
        }
      }
      if (rollbackErrors.length > 0) {
        throw new AggregateError(
          [error, ...rollbackErrors],
          'complete GLB 资源作用域构建失败且租约回滚不完整',
        )
      }
      throw error
    }
  }
}
